<template>
    <div class="container mx-auto px-4">
        <h1 class="font-serif mb-8 md:text-5xl text-2xl text-center">
            {{ __('Rendelések') }}
        </h1>

        <nav class="flex mb-8">
            <router-link class="bg-white border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row -mr-px p-2 rounded-bl rounded-tl text-center text-slate-800 text-sm"
                         v-bind:to="{ name : 'orders.table' }"
            >
                <c-icon class="h-6 md:mr-2 stroke-slate-800 w-6" icon="columns" />
                Táblázatos nézet
            </router-link>
            <router-link class="bg-white border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row -mr-px p-2 text-center text-slate-800 text-sm"
                         v-bind:to="{ name : 'orders.daily' }"
            >
                <c-icon class="h-6 md:mr-2 stroke-slate-800 w-6" icon="users" />
                Napi bontás
            </router-link>
            <span class="bg-highlight border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row -mr-px p-2 text-center text-sm text-white">
                <c-icon class="h-6 md:mr-2 stroke-white w-6" icon="user" />
                Megrendelő szerint
            </span>
            <router-link class="bg-white border border-slate-200 cursor-pointer flex flex-col items-center justify-center md:flex-row p-2 rounded-br rounded-tr text-center text-slate-800 text-sm"
                         v-bind:to="{ name : 'orders.byproduct' }"
            >
                <c-icon class="h-6 md:mr-2 stroke-slate-800 w-6" icon="clipboard" />
                Termék összesítő
            </router-link>
        </nav>

        <div class="flex items-center mb-8">
            <div class="flex flex-col md:flex-row w-full">
                <c-control-autocomplete class="mb-2 md:mb-0 md:w-1/2 mr-2" class-field="!h-10 !text-lg" class-group="bg-white border pl-2 rounded-none" icon="search" name="customer"
                                        v-bind:label-field="(data) => data.lastname + ' ' + data.firstname"
                                        v-bind:placeholder="__('Megrendelő keresése...')"
                                        v-bind:source-get="customerGet"
                                        v-bind:source-search="customerSearch"
                                        v-model="customer"
                                        v-on:update:model-value="getOrders"
                />

                <c-control-date class="mb-2 md:mb-0 md:w-1/4 mr-2" class-field="!h-10 !text-lg" class-group="bg-white border pl-2 rounded-none" icon="play" model-type="timestamp" name="dateFrom"
                                v-bind:enable-time-picker="false"
                                v-bind:format="localizationStore.dateFormat"
                                v-bind:placeholder="__('Dátum -tól')"
                                v-model="dateFrom"
                                v-on:update:model-value="getOrders"
                />

                <c-control-date class="md:w-1/4 mr-2" class-field="!h-10 !text-lg" class-group="bg-white border pl-2 rounded-none" icon="skip-forward" model-type="timestamp" name="dateTo"
                                v-bind:enable-time-picker="false"
                                v-bind:format="localizationStore.dateFormat"
                                v-bind:placeholder="__('Dátum -ig')"
                                v-model="dateTo"
                                v-on:update:model-value="getOrders"
                />
            </div>

            <c-button class="bg-slate-800 ml-4 shrink-0 text-white"
                      router
                      v-bind:disabled="!customer || tableData.items.length === 0"
                      v-on:click="exportXls"
            >
                <c-icon class="h-4 mr-2 stroke-white w-4" icon="download" />{{ __('Exportálás') }}
            </c-button>
        </div>

        <c-data-table responsive-breakpoint="lg"
                      v-bind:data="tableData"
                      v-if="customer && tableData.items.length > 0"
                      v-on:sort-table="sortTable"
        >
            <template v-slot:item.date="{ item }">
                {{ item.values.date.toFormat(localizationStore.dateFormat) }}
            </template>
            <template v-slot:item.products="{ item }">
                <div>
                    <template v-for="product in item.values.items">
                        <div class="flex flex-col md:flex-row md:items-center mb-2 mr-4 pb-2 peer"
                             v-if="product.day === item.values.day"
                        >
                            <img class="mr-4 max-w-32 w-full"
                                 v-bind:alt="product.product.name"
                                 v-bind:src="product.product.picture"
                            />
                            <div class="mr-4">
                                <strong class="block text-slate-800 text-sm">{{ product.product.name }}</strong>
                                <span class="block max-w-96 text-slate-500 text-xs">{{ product.product.description }}</span>
                            </div>
                            <strong class="md:ml-auto mr-4">{{ product.quantity }}x</strong>
                            <span class="line-through mr-2" v-if="product.price_original !== product.price_gross">
                                {{ currencyStore.format(product.price_original, 'huf') }}
                            </span>
                            <span>{{ currencyStore.format(product.price_gross, 'huf') }}</span>
                        </div>
                    </template>
                    <div class="border-t flex flex-col md:flex-row md:items-center mr-4 mt-2 pt-2"
                         v-if="item.values.value >= currencyStore.shipping_limits.zone && item.values.value < currencyStore.shipping_limits.free"
                    >
                        <div class="mr-4 max-w-32 w-full" />
                        <div class="mr-4">
                            <strong class="block text-slate-800 text-sm">{{ __('Kiszállítási költség') }}</strong>
                        </div>
                        <div class="md:ml-auto">
                            <strong class="mr-4">1x</strong>
                            <span>
                                {{ currencyStore.format(item.values.user.shipping_cost ?? currencyStore.shipping_costs[item.values.user.shipping_zone ?? 'default'], 'huf') }}
                            </span>
                        </div>
                    </div>
                    <div class="border-t flex flex-col md:flex-row md:items-center mr-4 mt-2 pt-2"
                         v-else
                    >
                        <div class="mr-4 max-w-32 w-full" />
                        <div class="mr-4">
                            <strong class="block text-slate-800 text-sm"
                                    v-if="item.values.value < currencyStore.shipping_limits.zone"
                            >
                                {{ __('Személyes átvétel') }}
                            </strong>
                            <strong class="block text-slate-800 text-sm"
                                    v-else
                            >
                                {{ __('Ingyenes szállítás') }}
                            </strong>
                        </div>
                    </div>
                </div>
            </template>
            <template v-slot:item.value="{ item }">
                <strong>{{ currencyStore.format(item.values.value, 'huf') }}</strong>
            </template>
        </c-data-table>

        <span class="block font-serif text-center text-2xl" v-else-if="!customer">
            {{ __('A találatok megjelenítéséhez előbb válassz ki egy megrendelőt') }}
        </span>

        <span class="block font-serif text-center text-2xl" v-else>{{ __('Nincs találat') }}</span>
    </div>
</template>

<script setup lang="ts">
    import { inject, ref } from 'vue';
    import {
        CButton,
        CControlAutocomplete,
        CControlDate,
        CIcon,
        CDataTable,
    } from '@teamfurther/cinderblock';
    import { DateTime } from 'luxon';
    import OrderRepository from '../../repositories/OrderRepository';
    import UserRepository from '../../repositories/UserRepository';
    import { useCurrencyStore } from '../../stores/currency';
    import { useLocalizationStore } from '../../stores/localization';
    import { useRequestStore } from '../../stores/request';
    import { useSessionStore } from '../../stores/session';

    const __ = inject('__');
    const currencyStore = useCurrencyStore();
    const customer = ref();
    const dateFrom = ref(null);
    const dateTo = ref(null);
    const localizationStore = useLocalizationStore();
    const orderRepository = new OrderRepository();
    const requestStore = useRequestStore();
    const sessionStore = useSessionStore();
    const sort = ref<string>('-created_at');
    const tableData = ref<object>({
        columnNames: {
            date: __('Dátum'),
            products: __('Termékek'),
            value: __('Termékek értéke'),
        },
        defaultSort: '-created_at',
        items: [],
        sortableColumns: [],
    });
    const user = ref(sessionStore.user);
    const userRepository = new UserRepository();

    function calculateTotalPrice(items: any[], day: string, shippingCost: number) {
        let total = shippingCost;

        items.forEach((item) => {
            if (item.day === day) {
                total += item.price_gross * item.quantity;
            }
        });

        return total;
    }

    async function customerGet(customerId) {
        if (!customerId.value) {
            return null;
        }

        return await userRepository.show(customerId.value.id);
    }

    async function customerSearch(val) {
        return await userRepository.index({
            filters : {
                search: val
            }
        });
    }

    async function exportXls() {
        requestStore.post('orders/export/byuser', {
            after: dateFrom.value ? DateTime.fromMillis(dateFrom.value).toFormat('yyyy-MM-dd') : null,
            before: dateTo.value ? DateTime.fromMillis(dateTo.value).toFormat('yyyy-MM-dd') : null,
            user_id: customer.value.id,
        }).then((response : any) => {
            const blob = new Blob([
                new Uint8Array([0xEF, 0xBB, 0xBF]), // UTF-8 BOM
                response.data
            ]);
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.style.display = 'none';
            a.href = url;

            a.download = 'megrendelo_' + customer.value.lastname + '_' + customer.value.firstname + '_' + (dateFrom.value ? dateFrom.value.toFormat('yyyy-MM-dd') : 'all') + '-' + (dateTo.value ? dateTo.value.toFormat('yyyy-MM-dd') : 'all') + '.csv';
            document.body.appendChild(a);
            a.click();
            window.URL.revokeObjectURL(url);
        });
    }

    async function getOrders() {
        if (!customer.value) {
            return;
        }

        const result = await orderRepository.index({
            filters: {
                after: dateFrom.value ? DateTime.fromMillis(dateFrom.value) : null,
                before: dateTo.value ? DateTime.fromMillis(dateTo.value) : null,
                user_id: customer.value.id,
            },
            include: ['items', 'items.product', 'user', 'user.group'],
            sort: sort.value,
        });

        let data = {};

        result.forEach(order => {
           order.items.forEach(product => {
               const date = DateTime.fromObject({ weekYear: order.year, weekNumber: order.week, weekDay: DateTime.fromFormat(product.day, 'EEEE').weekday });

               if (
                   (!dateFrom.value || date.ordinal >= DateTime.fromMillis(dateFrom.value).ordinal)
                   && (!dateTo.value || date.ordinal <= DateTime.fromMillis(dateTo.value).ordinal)
               ) {
                   data[date] = {values: {...order, date: date, day: product.day}};
               }
           });
        });

        tableData.value.items = Object.values(data);
    }

    async function sortTable(sortBy: string) {
        sort.value = sortBy;

        await getOrders();
    }
</script>
